// ─────────────────────────────────────────────
// format — locale-aware date/number formatting
//
// Centralizes Intl formatting so every screen renders
// dates in the active UI language (S-153). Serbian uses
// the Latin script, matching the rest of the sr locale.
// ─────────────────────────────────────────────

import type { Language } from "@/types";

const LOCALE_TAGS: Record<Language, string> = {
  en: "en-US",
  sr: "sr-Latn-RS",
};

export function localeTag(lang: Language): string {
  return LOCALE_TAGS[lang] ?? LOCALE_TAGS.en;
}

function toDate(value: string | number | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format a date (no time). Returns "—" for missing/invalid input. */
export function formatDate(
  value: string | number | Date | null | undefined,
  lang: Language,
  options?: Intl.DateTimeFormatOptions
): string {
  if (value == null || value === "") return "-";
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleDateString(
    localeTag(lang),
    options ?? { year: "numeric", month: "short", day: "numeric" }
  );
}

/** Format a date + time. Returns "—" for missing/invalid input. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  lang: Language,
  options?: Intl.DateTimeFormatOptions
): string {
  if (value == null || value === "") return "-";
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleString(
    localeTag(lang),
    options ?? { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
  );
}

/** Format a number with locale-appropriate separators. */
export function formatNumber(
  value: number | null | undefined,
  lang: Language,
  options?: Intl.NumberFormatOptions
): string {
  if (value == null || Number.isNaN(value)) return "-";
  return value.toLocaleString(localeTag(lang), options);
}
